import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hideConfirm, showConfirm } from "../../reducer/confirmSlice";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { changeLoginState } from "../../reducer/loginSlice";
import { showAlert } from "../../reducer/alertSlice";
import { func } from "prop-types";


export const useRowNo = () => {
    function getRowNo(currentPage, total, perPage, idx) {
        const startNo = total - (currentPage - 1) * perPage;
        const rowNo = startNo - idx;
        return rowNo;
    }
    
    return {getRowNo};
}


export const useCheckDirtyField = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const checkFieldConfirm = (dirty) => {
        if (dirty) {
            dispatch(showConfirm({
                msg: <>작성 중인 내용이 저장되지 않았습니다.<br/>정말 나가시겠어요?</>,
                btnText: "확인",
                func: () => {
                    navigate(-1);
                    dispatch(hideConfirm());
                }
            }))
        } else {
            navigate(-1);
        }
    }

    return {checkFieldConfirm}
}

export const useCheckByte = (str="", maxByte=0) => {
    let totalByte = 0;
    let strResult = "";

    for(let i = 0; i < str.length; i++) {
        const currentByte = str.charCodeAt(i);
        if (totalByte < maxByte) {
            strResult += str[i];
        }
        if (currentByte > 128){
            totalByte += 2;
        } else {
            totalByte++;
        }
    }

    if (maxByte < totalByte) {
        strResult += "...";
    }

    return {totalByte, strResult};
}

export const useMasking = () => {
    function masking(str="", type="name") {
        let result = ""
        const length = str.length;
        let isKo = false;
        for(let i = 0; i < length; i++) {
            const currentByte = str.charCodeAt(i);
            if (currentByte > 128){
                isKo = true;
            }
        }

        switch (type) {
            case "name":
                if (isKo) {
                    if (length == 1) {
                        result = "*";
                    } else if (length == 2) {
                        result = str[0] + "*";
                    } else if (length == 3) {
                        result = str[0] + "*" + str[2];
                    } else if (length >= 4) {
                        let asterisk = "";
                        for (let i = 0; i < length - 2; i++) {
                            asterisk += "*"
                        }
                        result = str[0] + asterisk + str[str.length - 1];
                    }
                } else {
                    let maskingStr = "";
                    for(let i = 0; i < length - 4; i++) {
                        if (str[i] != " ") {
                            maskingStr += "*";
                        } else {
                            maskingStr += " ";
                        }
                    }
                    result = str.substring(0, 4) + maskingStr;
                }
                break;
            case "phone":
                result = str;
                const splitPhone = str.split("-");
                if (splitPhone.length < 3) {
                    result = str.replace(/[0-9]/g, "*");
                } else {
                    result = splitPhone[0] + "-****-" + splitPhone[2];
                }
                break;
            case "birth":
                const splitBirth = str.split("-");
                result = splitBirth[0] + "-**-**";
                break;
        
            default:
                break;
        }

        if (length == 0) {
            return "-";
        }

        return result;
    }

    return {masking};
}

export const useLogout = () => {
    const dispatch = useDispatch();
    return (useQuery({
        queryKey: ['logout'],
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        queryFn: () => axios("/logout", {
            method: "GET",
        }).then(res => {
            if (res.status == 200) {
                axios.defaults.headers.common["Authorization"] = "";
                sessionStorage.removeItem("isAuthenticated");
                dispatch(changeLoginState({
                    token: null,
                    header: null,
                    menuList: null,
                    admin: null,
                }));
                return true;
            }
        }),
        enabled: false
    }));
}

export const useRefresh = () => {
    const logout = useLogout();
    const dispatch = useDispatch();

    return (
        useQuery({
            queryKey: ['refresh'],
            retry: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            queryFn: () => {
                return axios("/refresh", {
                    method: "GET",
                    withCredentials: true,
                }).then(res => {
                    if (res.status == 200) { // 재발급 성공 (로그인 유지)
                        const accessToken = res.data.data.accessToken;
                        axios.defaults.headers.common["Authorization"] = "Bearer " + accessToken;
                        axios.defaults.withCredentials = true;
                        dispatch(changeLoginState({
                            token: accessToken,
                            header: res.data.data.header,
                            menuList: res.data.data.menuList,
                            admin: res.data.data.admin,
                            isSuperAdmin: res.data.data.isSuperAdmin
                        }));
                    } else {
                        throw Error();
                    }
                    return res;
                }).catch(e => {
                    dispatch(showAlert({
                        msg: <>세션이 만료되었습니다.<br/>계속하려면 다시 로그인해 주세요.</>
                    }))
                    logout.refetch(); // 로그아웃 처리
                    return e;
                });
            },
            enabled: false
        })
    )
}

export const useRequestCertification = (phone ="", type="", id="") => {
    const dispatch = useDispatch();
    return (useQuery({
        queryKey: ['requestCertification'],
        refetchOnWindowFocus: false,
        retry: 0, 
        queryFn: () => {
            return axios("/certification/request/" + type, {
                method: "POST",
                data: type == "3" ?
                {
                    phone: phone,
                    id: id
                }
                : {
                    phone: phone
                }
            }).then(result => {
                return result;
            }).catch((e) => {
                let msg;
                switch (e.response.data.code) {
                    case -18:
                        msg = <>등록되지 않은 계정입니다.<br/>번호를 다시 한 번 확인해주세요.</>;
                        break;
                    case -17:
                        msg = <>이미 등록 또는 중복된 휴대폰번호가 있습니다.<br/>동일 휴대폰번호로 여러 계정에 등록 불가합니다.</>;
                        break;
                        
                    default:
                        msg = <>오류가 발생했습니다. <br/>잠시 후 다시 시도해주세요.<br/>Error Code: {e.response.data.code}</>;
                        break;
                }
                dispatch(showAlert({
                    msg: <>{msg}</>
                }))

                return e;
            })
        },
        enabled: false
    }))
}

export const useCertification = (phone ="", certificationNo="", retryFunc = () => {}) => {
    const dispatch = useDispatch();
    
    return (useQuery({
        queryKey: ['certification'],
        refetchOnWindowFocus: false,
        retry: 0, 
        queryFn: () => {
            return axios("/certification/", {
                method: "POST",
                data: {
                    phone: phone,
                    certificationNo: certificationNo
                }
            }).then(result => {
                return result;
            }).catch((e) => {
                let msg;
                if (e.response.data.code == -23) {
                    msg = <>인증번호 입력 시간이 초과되었습니다. <br/>재요청 후 다시 입력해주세요.</>;
                    dispatch(showConfirm({
                        msg: msg,
                        btnText: "재요청", 
                        func: () => {
                            dispatch(hideConfirm());
                            retryFunc();
                        }
                    }))
                } else {
                    switch (e.response.data.code) {
                        case -11:
                            msg = <>인증번호가 일치하지 않습니다.<br/>다시 한 번 확인해주세요.</>;
                            break;
                            
                        default:
                            msg = <>{e.response.data.msg.ko}</>;
                            break;
                    }
                    dispatch(showAlert({
                        msg: <>{msg}</>
                    }))
                }

                throw Error();
            })
        },
        enabled: false
    }))
}